/**
 * Notifications inbox slide-over (v1.1.1-b3).
 *
 * Opened from the bell in AppShell. Right-side Sheet that lists:
 *   1. Pending friend requests with inline Accept / Decline
 *   2. All other notifications (research updates, billing, event
 *      reminders, system) with optional deep links
 *
 * Backends in use (J10, audited as real):
 *   GET  /api/notifications        list (newest first, max 50)
 *   POST /api/notifications/read   mark by id(s) or "all"
 *   GET  /api/friends/pending      pending requests addressed to me
 *   POST /api/friends/:id/accept   accept a pending request
 *   POST /api/friends/:id/decline  decline a pending request
 *
 * The bell unread badge reads from the same /api/notifications query
 * (counting items where readAt is null). When the panel closes we
 * invalidate the queries so a returning trainer sees fresh counts on
 * next open. React Query also auto-refetches every 60 seconds in the
 * background.
 */
import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { NexButton } from "@/components/ui/NexButton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useSession } from "@/components/SessionProvider";
import { isFriendLimitError, errorMessage } from "@/lib/apiError";
import { normalizeHashHref, hashNavigate } from "@/lib/hashRouter";
import { renderShareCard, APP_STORE_URL, type MilestoneCardSpec } from "@/lib/shareCard";
import { shareImageBlob } from "@/lib/shareImage";
import {
  UserPlus, Inbox, ChevronRight, ChevronsRight, Check, X, Trash2,
  Bell, ShieldCheck, Sparkles, Star, CreditCard, Handshake, ArrowLeftRight, Share2,
} from "lucide-react";

// Z3b B1: horizontal drag past this distance (px) closes the right-side
// notifications sheet. A fast flick also closes via the velocity check.
const SWIPE_CLOSE_PX = 80;
const SWIPE_CLOSE_VELOCITY = 0.5; // px per ms

// ── Server response types ────────────────────────────────────────────────────

interface NotificationRow {
  id: number;
  accountId: number;
  kind: string;            // 'event_reminder' | 'trade_match' | 'friend_accepted' | etc.
  title: string;
  body: string;
  href: string | null;
  // Optional JSON string (notifications.metadata). For friend_accepted:
  // { friend_id, friend_display_name }. Tap navigation uses `href`, so this
  // is currently informational; PR-B reads it for the tease+gate view.
  metadata: string | null;
  readAt: number | null;
  createdAt: number;
}

interface FriendshipPending {
  id: number;
  ownerId: number;
  friendId: number;
  status: string;
  nickname: string | null;
  createdAt: number;
  acceptedAt: number | null;
  friend: {
    id: number;
    username: string | null;
    trainerName: string | null;
  } | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 0) return "just now";
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 4) return `${wk}w ago`;
  return new Date(ms).toLocaleDateString();
}

function iconForKind(kind: string): typeof Bell {
  switch (kind) {
    case "friend_dex_progress": return Sparkles;
    case "trade_alert":         return ArrowLeftRight;
    case "trade_agreed":        return Handshake;
    case "trade_completed":     return Check;
    case "trade_declined":
    case "trade_cancelled":     return X;
    // Server writes snake_case `collection_milestone`; the camelCase form only
    // appears in the push payload. Match both so the Star icon renders.
    case "collection_milestone":
    case "collectionMilestone": return Star;
    // research_digest is retired as an event, but legacy opt-out-confirmation
    // rows may still be in inboxes within the 7-day window.
    case "research_digest":  return ShieldCheck;
    case "billing":
    case "subscription":     return CreditCard;
    case "friend_request":   return UserPlus;
    case "friend_accepted":  return Handshake;
    default:                 return Bell;
  }
}

// ── Public hook for the bell badge ───────────────────────────────────────────
//
// Renders the unread count for the header bell. We pull from the same
// /api/notifications query (counting readAt === null) plus a small bump
// for any pending friend requests so the trainer notices both.

export function useInboxUnreadCount(): number {
  const notifQ = useQuery<{ notifications: NotificationRow[] }>({
    queryKey: ["/api/notifications"],
    refetchInterval: 60_000,
  });
  const pendingQ = useQuery<{ pending: FriendshipPending[] }>({
    queryKey: ["/api/friends/pending"],
    refetchInterval: 60_000,
  });
  const unreadNotifications = (notifQ.data?.notifications ?? []).filter((n) => n.readAt == null).length;
  const pendingRequests = pendingQ.data?.pending?.length ?? 0;
  return unreadNotifications + pendingRequests;
}

// ── Panel component ─────────────────────────────────────────────────────────

interface NotificationsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationsPanel({ open, onOpenChange }: NotificationsPanelProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { openPaywall } = useSession();

  // Source queries. We always run both so the panel renders instantly
  // when opened (they were already fetched by useInboxUnreadCount on the
  // bell). React Query dedupes on the queryKey.
  const notifQ = useQuery<{ notifications: NotificationRow[] }>({
    queryKey: ["/api/notifications"],
    enabled: open,
  });
  const pendingQ = useQuery<{ pending: FriendshipPending[] }>({
    queryKey: ["/api/friends/pending"],
    enabled: open,
  });

  const notifications = notifQ.data?.notifications ?? [];
  const pending = pendingQ.data?.pending ?? [];
  const unreadCount = notifications.filter((n) => n.readAt == null).length;
  const readCount = notifications.filter((n) => n.readAt != null).length;
  const totalItems = notifications.length + pending.length;
  const isLoading = (notifQ.isLoading && !notifQ.data) || (pendingQ.isLoading && !pendingQ.data);

  // ── Mutations ───────────────────────────────────────────────────────────

  const acceptMutation = useMutation({
    mutationFn: async (friendshipId: number) => {
      const res = await apiRequest("POST", `/api/friends/${friendshipId}/accept`);
      return res.json();
    },
    onMutate: async (friendshipId) => {
      // Optimistic: drop the row immediately so the trainer sees the
      // panel update without a round-trip wait.
      await queryClient.cancelQueries({ queryKey: ["/api/friends/pending"] });
      const previous = queryClient.getQueryData<{ pending: FriendshipPending[] }>([
        "/api/friends/pending",
      ]);
      if (previous) {
        queryClient.setQueryData(["/api/friends/pending"], {
          pending: previous.pending.filter((p) => p.id !== friendshipId),
        });
      }
      return { previous };
    },
    onError: (err, _id, ctx) => {
      // Roll the optimistic removal back first — the request is still pending.
      if (ctx?.previous) queryClient.setQueryData(["/api/friends/pending"], ctx.previous);
      // At the Basic friend cap: sell the upgrade rather than telling the user
      // to "try again in a moment", which would never work.
      if (isFriendLimitError(err)) {
        openPaywall("notifications", errorMessage(err));
        return;
      }
      toast({
        title: "Could not accept",
        description: "Try again in a moment.",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      // Refresh the live friends list so the new friend appears there.
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/pending"] });
    },
  });

  const declineMutation = useMutation({
    mutationFn: async (friendshipId: number) => {
      const res = await apiRequest("POST", `/api/friends/${friendshipId}/decline`);
      return res.json();
    },
    onMutate: async (friendshipId) => {
      await queryClient.cancelQueries({ queryKey: ["/api/friends/pending"] });
      const previous = queryClient.getQueryData<{ pending: FriendshipPending[] }>([
        "/api/friends/pending",
      ]);
      if (previous) {
        queryClient.setQueryData(["/api/friends/pending"], {
          pending: previous.pending.filter((p) => p.id !== friendshipId),
        });
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(["/api/friends/pending"], ctx.previous);
      toast({
        title: "Could not decline",
        description: "Try again in a moment.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/pending"] });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: async (input: { ids: "all" | number[] }) => {
      const res = await apiRequest("POST", "/api/notifications/read", input);
      return res.json();
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ["/api/notifications"] });
      const previous = queryClient.getQueryData<{ notifications: NotificationRow[] }>([
        "/api/notifications",
      ]);
      if (previous) {
        const now = Date.now();
        const next = previous.notifications.map((n) => {
          const hit = input.ids === "all" || input.ids.includes(n.id);
          return hit && n.readAt == null ? { ...n, readAt: now } : n;
        });
        queryClient.setQueryData(["/api/notifications"], { notifications: next });
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(["/api/notifications"], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  // Z3b B2: delete every read notification. Optimistically drop the read rows
  // so the list updates instantly, revert + toast on server error. The server
  // endpoint (DELETE /api/notifications/read) is added by the Z3a server PR.
  const deleteReadMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/notifications/read");
      return res.json();
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["/api/notifications"] });
      const previous = queryClient.getQueryData<{ notifications: NotificationRow[] }>([
        "/api/notifications",
      ]);
      if (previous) {
        queryClient.setQueryData(["/api/notifications"], {
          notifications: previous.notifications.filter((n) => n.readAt == null),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(["/api/notifications"], ctx.previous);
      toast({
        title: "Could not delete read",
        description: "Try again in a moment.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  // Z3b B1: swipe-right-to-close. Tracks a single horizontal pointer drag on
  // the sheet body. A drag that is mostly vertical is ignored (the scroll
  // list keeps it). Past the distance threshold OR on a fast right flick we
  // close; otherwise the sheet snaps back to 0.
  const dragStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const dragLocked = useRef<"horizontal" | "vertical" | null>(null);
  const [dragX, setDragX] = useState(0);

  function onTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    dragStart.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    dragLocked.current = null;
  }

  function onTouchMove(e: React.TouchEvent) {
    const start = dragStart.current;
    if (!start) return;
    const t = e.touches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Lock the axis on first meaningful movement so a vertical scroll never
    // turns into a close, and a horizontal swipe never fights the scroller.
    if (dragLocked.current == null && Math.abs(dx) + Math.abs(dy) > 8) {
      dragLocked.current = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
    }
    if (dragLocked.current !== "horizontal") return;
    // Only rightward drag closes; clamp leftward to 0 so the panel never
    // detaches from its right edge.
    setDragX(Math.max(0, dx));
  }

  function onTouchEnd() {
    const start = dragStart.current;
    const offset = dragX;
    dragStart.current = null;
    dragLocked.current = null;
    setDragX(0);
    if (!start) return;
    const elapsed = Math.max(1, Date.now() - start.t);
    const velocity = offset / elapsed;
    if (offset > SWIPE_CLOSE_PX || velocity > SWIPE_CLOSE_VELOCITY) {
      onOpenChange(false);
    }
  }

  function handleNotificationTap(n: NotificationRow) {
    if (n.readAt == null) markReadMutation.mutate({ ids: [n.id] });
    if (n.href) {
      // External absolute URLs vs in-app hash routes. normalizeHashHref
      // collapses the various stored shapes (/#/friends, #/friends,
      // /friends) into one clean route so we never emit a #/#/... double
      // hash (the old friend-add 404). External URLs open in the same tab
      // so the user can return to the inbox afterwards.
      const { external, to } = normalizeHashHref(n.href);
      if (external) {
        window.location.href = to;
      } else {
        hashNavigate(to);
      }
      onOpenChange(false);
    }
  }

  const empty = !isLoading && totalItems === 0;

  const pendingSorted = useMemo(
    () => [...pending].sort((a, b) => b.createdAt - a.createdAt),
    [pending],
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          // Refresh on close so the badge stays in sync with what the
          // trainer just acted on.
          queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
          queryClient.invalidateQueries({ queryKey: ["/api/friends/pending"] });
        }
      }}
    >
      <SheetContent
        side="right"
        hideClose
        data-testid="sheet-notifications"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="w-[85vw] sm:max-w-[420px] p-0 flex flex-col gap-0 border-l bg-[var(--surface-base)] border-[var(--border-default)]"
        style={{
          transform: dragX > 0 ? `translateX(${dragX}px)` : undefined,
          // No transition while actively dragging (finger-tracked); the snap
          // back to 0 on release animates via this transition.
          transition: dragX > 0 ? "none" : "transform 220ms ease-out",
        }}
      >
        <SheetHeader
          className="px-5 pb-4 border-b border-[var(--border-subtle)] text-left"
          style={{ paddingTop: "calc(var(--nx-safe-top) + 1rem)" }}
        >
          {/* Dismiss affordance replacing the sheet's default corner X (which
              overlapped "Delete read" on Android and sat unreachably far into
              the top-right safe area on iPhone). This is in-flow — respects the
              header's safe-area padding so it's always tappable — and doubles as
              a hint for the existing swipe-right-to-close gesture. */}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="button-close-notifications"
            aria-label="Close notifications"
            className="mb-2 ml-auto inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.14em] hover-elevate rounded-md px-2 py-1 text-[var(--text-secondary)]"
          >
            Slide to close <ChevronsRight className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-center justify-between gap-3">
            <SheetTitle
              className="text-base font-bold inline-flex items-center gap-2 text-[var(--text-primary)]"
            >
              <Inbox className="w-4 h-4 text-[var(--accent-fill)]" />
              Inbox
            </SheetTitle>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  data-testid="button-mark-all-read"
                  onClick={() => markReadMutation.mutate({ ids: "all" })}
                  className="text-[10.5px] font-bold uppercase tracking-[0.14em] inline-flex items-center gap-1 hover-elevate rounded-md px-2 py-1 text-[var(--accent-fill)]"
                >
                  <Check className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button
                data-testid="button-delete-read"
                onClick={() => deleteReadMutation.mutate()}
                disabled={readCount === 0 || deleteReadMutation.isPending}
                className="text-[10.5px] font-bold uppercase tracking-[0.14em] inline-flex items-center gap-1 hover-elevate rounded-md px-2 py-1 text-[var(--text-secondary)] disabled:opacity-40 disabled:pointer-events-none"
              >
                <Trash2 className="w-3 h-3" /> Delete read
              </button>
            </div>
          </div>
          <SheetDescription className="sr-only">
            Friend requests and notifications.
          </SheetDescription>
        </SheetHeader>

        <div
          className="flex-1 overflow-y-auto"
          data-testid="inbox-scroll"
          // Pad the scroll body past the iOS home indicator so the last
          // notification row isn't clipped beneath it on full-height sheets.
          style={{ paddingBottom: "var(--nx-safe-bottom)" }}
        >
          {isLoading && (
            <div className="px-5 py-6 text-[12px] text-muted-foreground">Loading inbox</div>
          )}

          {/* Friend requests section */}
          {pendingSorted.length > 0 && (
            <section className="px-5 py-4" data-testid="section-friend-requests">
              <p
                className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2 text-[var(--accent-fill)]"
              >
                Friend requests
              </p>
              <ul className="space-y-2">
                {pendingSorted.map((p) => (
                  <FriendRequestRow
                    key={p.id}
                    pending={p}
                    onAccept={() => acceptMutation.mutate(p.id)}
                    onDecline={() => declineMutation.mutate(p.id)}
                    busy={
                      (acceptMutation.isPending && acceptMutation.variables === p.id) ||
                      (declineMutation.isPending && declineMutation.variables === p.id)
                    }
                  />
                ))}
              </ul>
            </section>
          )}

          {/* Other notifications section */}
          {notifications.length > 0 && (
            <section className="px-5 py-4" data-testid="section-notifications">
              <p
                className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2 text-[var(--text-secondary)]"
              >
                Notifications
              </p>
              <ul className="space-y-2">
                {notifications.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notif={n}
                    onTap={() => handleNotificationTap(n)}
                  />
                ))}
              </ul>
            </section>
          )}

          {/* Empty state */}
          {empty && (
            <div
              className="px-5 py-10 flex flex-col items-center text-center"
              data-testid="inbox-empty"
            >
              <div className="opacity-60 mb-3">
                <Inbox className="w-12 h-12 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                All caught up
              </p>
              <p className="text-[11.5px] text-muted-foreground mt-1 max-w-[240px]">
                When friends invite you or there's something worth knowing, it lands here.
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Row components ──────────────────────────────────────────────────────────

function FriendRequestRow({
  pending,
  onAccept,
  onDecline,
  busy,
}: {
  pending: FriendshipPending;
  onAccept: () => void;
  onDecline: () => void;
  busy: boolean;
}) {
  const name = pending.friend?.trainerName ?? pending.friend?.username ?? "A trainer";
  const handle = pending.friend?.username ? `@${pending.friend.username}` : null;
  return (
    <li
      className="rounded-xl p-3 bg-[var(--surface-raised)] border border-[var(--surface-chip-border)]"
      data-testid={`friend-request-${pending.id}`}
    >
      <div className="flex items-start gap-2.5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-[var(--accent-subtle)] border border-[var(--surface-chip-border)] text-[var(--accent-fill)]"
        >
          <UserPlus className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold leading-tight text-[var(--text-primary)]">
            {name}
            {handle && (
              <span className="text-muted-foreground font-normal ml-1.5">{handle}</span>
            )}
          </p>
          <p className="text-[10.5px] text-muted-foreground mt-0.5">
            Wants to be friends, {timeAgo(pending.createdAt)}.
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        {/* b13f: route through NexButton so the Accept/Decline pair is
            theme-aware (was a hardcoded neon-green gradient + #0B0E1A text
            that ignored light mode). */}
        <NexButton
          size="sm"
          variant="primary"
          disabled={busy}
          onClick={onAccept}
          data-testid={`button-accept-${pending.id}`}
          className="h-8 rounded-full text-[11px] uppercase tracking-wide flex-1"
        >
          <Check className="w-3 h-3 mr-1" /> Accept
        </NexButton>
        <NexButton
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={onDecline}
          data-testid={`button-decline-${pending.id}`}
          className="h-8 rounded-full text-[11px] uppercase tracking-wide flex-1"
        >
          <X className="w-3 h-3 mr-1" /> Decline
        </NexButton>
      </div>
    </li>
  );
}

/** Title-case a raw region/filter slug ("johto" → "Johto", "shiny" → "Shiny"). */
function titleCase(s: string): string {
  return s
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Build a milestone share-card spec from a collection_milestone row's metadata. */
function milestoneSpecFromMetadata(metadata: string | null): MilestoneCardSpec {
  let region = "National";
  let filter = "Living";
  let threshold = 100;
  let percent = 100;
  if (metadata) {
    try {
      const m = JSON.parse(metadata) as {
        region?: string;
        filter?: string;
        threshold?: number;
        percent?: number;
      };
      if (m.region) region = titleCase(String(m.region));
      if (m.filter) filter = titleCase(String(m.filter));
      if (typeof m.threshold === "number") threshold = m.threshold;
      if (typeof m.percent === "number") percent = m.percent;
    } catch {
      /* keep defaults */
    }
  }
  const regionLabel = region.toLowerCase() === "all" ? "National" : region;
  const headline = threshold >= 100 ? `${regionLabel} Dex\nComplete!` : `${threshold}%\nComplete`;
  return {
    kind: "milestone",
    headline,
    subtitle: `${regionLabel} · ${filter}`,
    percentLabel: `${Math.round(percent)}%`,
  };
}

function NotificationRow({
  notif,
  onTap,
}: {
  notif: NotificationRow;
  onTap: () => void;
}) {
  const Icon = iconForKind(notif.kind);
  const unread = notif.readAt == null;
  const { toast } = useToast();
  const [sharing, setSharing] = useState(false);
  const isMilestone =
    notif.kind === "collection_milestone" || notif.kind === "collectionMilestone";

  async function shareMilestone(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    if (sharing) return;
    setSharing(true);
    try {
      const { blob } = await renderShareCard(milestoneSpecFromMetadata(notif.metadata));
      const outcome = await shareImageBlob({
        blob,
        fileName: "nexus-dex-milestone.png",
        title: "Nexus Dex",
        text: `I completed my Dex in Nexus Dex! Get it free on iPhone: ${APP_STORE_URL}`,
        dialogTitle: "Share your milestone",
      });
      if (outcome === "downloaded") {
        toast({ title: "Milestone card saved", description: "Share it anywhere." });
      }
    } catch {
      toast({
        title: "Couldn't build the card",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSharing(false);
    }
  }

  return (
    <li>
      <button
        type="button"
        onClick={onTap}
        data-testid={`notification-${notif.id}`}
        className={
          "w-full text-left rounded-xl p-3 hover-elevate flex items-start gap-2.5 border " +
          (unread
            ? "bg-[var(--accent-subtle)] border-[var(--surface-chip-border)]"
            : "bg-[var(--surface-raised)] border-[var(--border-subtle)]")
        }
      >
        <div
          className={
            "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border " +
            (unread
              ? "bg-[var(--accent-subtle)] border-[var(--surface-chip-border)] text-[var(--accent-fill)]"
              : "bg-[var(--surface-sunken)] border-[var(--border-subtle)] text-[var(--text-tertiary)]")
          }
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <p
              className="text-[13px] font-semibold leading-tight truncate text-[var(--text-primary)]"
            >
              {notif.title}
            </p>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {timeAgo(notif.createdAt)}
            </span>
          </div>
          {notif.body && (
            <p className="text-[11.5px] text-muted-foreground mt-0.5 line-clamp-2">
              {notif.body}
            </p>
          )}
          {isMilestone && (
            // Share the completion as a branded card. role=button (not a nested
            // <button>) so it lives inside the row button without invalid nesting;
            // stopPropagation keeps the row's deep-link tap separate.
            <span
              role="button"
              tabIndex={0}
              onClick={shareMilestone}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") shareMilestone(e);
              }}
              aria-label="Share milestone card"
              data-testid={`notification-share-${notif.id}`}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[10.5px] font-semibold cursor-pointer text-[var(--accent-fill)] bg-[var(--accent-subtle)] border border-[var(--surface-chip-border)]"
            >
              <Share2 className="w-3 h-3" /> {sharing ? "Building…" : "Share card"}
            </span>
          )}
        </div>
        {!isMilestone && notif.href && (
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-1" />
        )}
      </button>
    </li>
  );
}
